// Main game class
// Handles game loop, level loading, camera, and entity management

import { VectorRenderer } from "../graphics/VectorRenderer";
import { InputManager } from "./InputManager";
import { Player } from "../entities/Player";
import { Projectile } from "../entities/Projectile";
import { Explosion } from "../entities/Explosion";
import { LevelEntity } from "../entities/LevelEntity";
import { Terrain } from "../systems/Terrain";
import { Stars } from "../systems/Stars";
import { Camera } from "../systems/Camera";
import { GameState } from "../utils/types";
import { CONFIG } from "../config";
import { LevelLoader, LevelData } from "../levels";

export class Game {
  private canvas: HTMLCanvasElement;
  private renderer: VectorRenderer;
  private input: InputManager;

  // Game state
  private state: GameState;
  private score: number;
  private lives: number;

  // Current level
  private currentLevel: LevelData | null = null;
  private currentLevelId: string = "level-1-1";

  // Entities
  private player: Player;
  private projectiles: Projectile[];
  private explosions: Explosion[];
  private levelEntities: LevelEntity[]; // Static entities from level data

  // World systems
  private terrain: Terrain;
  private stars: Stars;
  private camera: Camera;

  // Timing
  private lastTime: number;

  // Performance tracking
  private frameTime: number = 0;
  private frameTimeAccum: number = 0; // Accumulated frame time for averaging
  private fps: number = 0;
  private fpsFrameCount: number = 0;
  private lastFpsUpdate: number = 0;
  private fpsUpdateInterval: number = 100; // Update every 100ms
  // Bound game loop to avoid creating new function every frame
  private boundGameLoop: (time: number) => void;

  // HUD elements (DOM)
  private scoreElement: HTMLElement | null;
  private livesElement: HTMLElement | null;
  private fuelElement: HTMLElement | null;
  private shieldElement: HTMLElement | null;
  private bombsElement: HTMLElement | null;
  private perfInfoElement: HTMLElement | null;

  // Loading state
  private isLoading: boolean = false;
  private loadingMessage: string = "";

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.renderer = new VectorRenderer(canvas);
    this.input = new InputManager();

    this.state = "menu";
    this.score = 0;
    this.lives = CONFIG.player.startingLives;

    // Initialize systems
    this.terrain = new Terrain(canvas.width, canvas.height);
    this.stars = new Stars(canvas.width, canvas.height, 60);
    this.camera = new Camera(canvas.width, canvas.height);

    // Initialize player at default position
    this.player = new Player(canvas.width / 2, canvas.height / 2, this.input);
    this.setupPlayerCallbacks();

    this.projectiles = [];
    this.explosions = [];
    this.levelEntities = [];

    this.lastTime = 0;

    // Get HUD elements
    this.scoreElement = document.getElementById("score");
    this.livesElement = document.getElementById("lives");
    this.fuelElement = document.getElementById("fuel");
    this.shieldElement = document.getElementById("shield");
    this.bombsElement = document.getElementById("bombs");
    this.perfInfoElement = document.getElementById("perf-info");

    // Bind game loop once to avoid creating new function every frame
    this.boundGameLoop = this.gameLoop.bind(this);
  }

  private setupPlayerCallbacks(): void {
    this.player.setOnFire(() => {
      const gun = this.player.getGunPosition();
      const bullet = new Projectile(gun.x, gun.y, gun.angle, "bullet");
      this.projectiles.push(bullet);
    });

    this.player.setOnBomb(() => {
      const pos = this.player.getPosition();
      this.explosions.push(new Explosion(pos.x, pos.y, "large"));
      // Clear enemy projectiles
      this.projectiles = this.projectiles.filter(
        (p) => p.getType() === "bullet",
      );
    });
  }

  start(): void {
    this.lastTime = performance.now();
    this.lastFpsUpdate = this.lastTime;
    requestAnimationFrame(this.boundGameLoop);
  }

  private gameLoop(currentTime: number): void {
    const frameStart = performance.now();

    const dt = Math.min((currentTime - this.lastTime) / 1000, 0.1);
    this.lastTime = currentTime;

    this.update(dt);
    this.render();

    // Calculate frame time and accumulate for averaging
    const thisFrameTime = performance.now() - frameStart;
    this.frameTimeAccum += thisFrameTime;

    // Update FPS counter and average frame time
    this.fpsFrameCount++;
    if (currentTime - this.lastFpsUpdate >= this.fpsUpdateInterval) {
      this.fps = (this.fpsFrameCount * 1000) / (currentTime - this.lastFpsUpdate);
      this.frameTime = this.frameTimeAccum / this.fpsFrameCount; // Average frame time
      this.frameTimeAccum = 0;
      this.fpsFrameCount = 0;
      this.lastFpsUpdate = currentTime;

      // Update DOM element
      if (this.perfInfoElement) {
        this.perfInfoElement.textContent = `FPS: ${this.fps.toFixed(1)} | Frame: ${this.frameTime.toFixed(4)}ms`;
      }
    }

    this.input.update();
    requestAnimationFrame(this.boundGameLoop);
  }

  private update(dt: number): void {
    // Handle pause toggle
    if (this.input.wasKeyPressed("KeyP")) {
      if (this.state === "playing") {
        this.state = "paused";
      } else if (this.state === "paused") {
        this.state = "playing";
      }
    }

    // ESC returns to menu from playing or paused
    if (this.input.wasKeyPressed("Escape")) {
      if (this.state === "playing" || this.state === "paused") {
        this.state = "menu";
      }
    }

    // Start game from menu
    if (this.state === "menu" && this.input.wasKeyPressed("Space")) {
      this.startNewGame();
    }

    // Open level editor from menu
    if (this.state === "menu" && this.input.wasKeyPressed("KeyE")) {
      window.location.href = "/editor/";
    }

    // Restart from game over
    if (this.state === "gameover" && this.input.wasKeyPressed("Space")) {
      this.startNewGame();
    }

    if (this.state !== "playing") return;

    // Update player
    this.player.update(dt);

    // Update camera to follow player
    const playerPos = this.player.getPosition();
    const playerVel = this.player.getVelocity();
    this.camera.update(dt, playerPos, playerVel);

    // Terrain collision - use swept detection to prevent tunneling at high speeds
    const shipPoints = this.player.getCollisionPoints();
    const collisionRadius = this.player.getCollisionRadius();
    const prevPos = this.player.getPreviousPosition();

    // First check swept collision (prevents tunneling through thin terrain)
    const sweptResult = this.terrain.checkSweptCollision(
      prevPos,
      playerPos,
      shipPoints,
      collisionRadius,
    );

    if (sweptResult.collision.collided) {
      // Move player back to safe position before collision
      this.player.setPosition(
        sweptResult.safePosition.x,
        sweptResult.safePosition.y,
      );

      // Calculate impact speed (component of velocity into the surface)
      const vel = this.player.getVelocity();
      const impactSpeed = Math.abs(
        vel.x * sweptResult.collision.normal.x +
          vel.y * sweptResult.collision.normal.y,
      );

      this.player.bounce(
        sweptResult.collision.normal.x,
        sweptResult.collision.normal.y,
      );

      // Shield damage proportional to impact speed (min 1, scales with speed)
      const damage = Math.max(1, Math.floor(impactSpeed * 0.5));
      this.player.takeDamage(damage);
    } else {
      // No swept collision, check regular collision (for when already overlapping)
      const collision = this.terrain.getShipCollision(
        shipPoints,
        playerPos,
        collisionRadius,
      );

      if (collision.collided) {
        // Calculate impact speed (component of velocity into the surface)
        const vel = this.player.getVelocity();
        const impactSpeed = Math.abs(
          vel.x * collision.normal.x + vel.y * collision.normal.y,
        );

        this.player.bounce(collision.normal.x, collision.normal.y);

        // Shield damage proportional to impact speed (min 1, scales with speed)
        const damage = Math.max(1, Math.floor(impactSpeed * 0.5));
        this.player.takeDamage(damage);
      }
    }

    // Update projectiles
    const levelWidth = this.terrain.getLevelWidth();
    const levelHeight = this.terrain.getLevelHeight();
    const offScreenMargin = 100; // Extra margin before destroying

    for (const proj of this.projectiles) {
      proj.update(dt);

      const currPos = proj.getPosition();

      // Destroy projectiles that are far off-screen
      if (
        currPos.x < -offScreenMargin ||
        currPos.x > levelWidth + offScreenMargin ||
        currPos.y < -offScreenMargin ||
        currPos.y > levelHeight + offScreenMargin
      ) {
        proj.destroy();
        continue;
      }

      // Check projectile-terrain collision using line from previous to current position
      // This prevents fast bullets from tunneling through walls
      const prevPos = proj.getPreviousPosition();

      if (
        this.terrain.checkLineCollision(prevPos, currPos) ||
        this.terrain.checkPointCollision(currPos)
      ) {
        proj.destroy();
        this.explosions.push(new Explosion(currPos.x, currPos.y, "small"));
      }
    }

    // Update explosions
    for (const exp of this.explosions) {
      exp.update(dt);
    }

    // Clean up destroyed entities
    this.projectiles = this.projectiles.filter((p) => p.isActive());
    this.explosions = this.explosions.filter((e) => e.isActive());
    this.levelEntities = this.levelEntities.filter((e) => e.isActive());

    // Update HUD
    this.updateHUD();
  }

  private render(): void {
    this.renderer.clear();

    if (this.state === "menu") {
      this.renderMenu();
      return;
    }

    if (this.state === "gameover") {
      this.renderGameOver();
      return;
    }

    if (this.isLoading) {
      this.renderLoading();
      return;
    }

    const camX = this.camera.getX();
    const camY = this.camera.getY();

    // Draw background stars (parallax)
    this.stars.render(this.renderer, camX * 0.6, camY * 0.6);

    // Draw terrain
    this.terrain.render(this.renderer, camX, camY);

    // Draw level entities (world space)
    for (const entity of this.levelEntities) {
      const pos = entity.getPosition();
      if (this.camera.isVisible(pos)) {
        this.renderEntityAtCamera(entity, camX, camY);
      }
    }

    // Draw projectiles (world space)
    for (const proj of this.projectiles) {
      const pos = proj.getPosition();
      if (this.camera.isVisible(pos)) {
        this.renderEntityAtCamera(proj, camX, camY);
      }
    }

    // Draw player (world space)
    this.renderPlayerAtCamera(camX, camY);

    // Draw explosions (world space) - with visibility culling
    for (const exp of this.explosions) {
      if (this.camera.isVisible(exp.getPosition(), 80)) {
        this.renderEntityAtCamera(exp, camX, camY);
      }
    }

    // Draw HUD (screen space - always on top)
    this.renderHUD();

    // Draw pause overlay
    if (this.state === "paused") {
      this.renderPauseOverlay();
    }
  }

  // Render an entity with camera offset
  private renderEntityAtCamera(
    entity: {
      getPosition: () => { x: number; y: number };
      render: (r: VectorRenderer) => void;
    },
    camX: number,
    camY: number,
  ): void {
    const ctx = this.renderer["ctx"] as CanvasRenderingContext2D;

    ctx.save();
    ctx.translate(-camX, -camY);
    entity.render(this.renderer);
    ctx.restore();

    // Invalidate renderer state cache after restore
    this.renderer.invalidateState();
  }

  // Special render for player with camera offset
  private renderPlayerAtCamera(camX: number, camY: number): void {
    const ctx = this.renderer["ctx"] as CanvasRenderingContext2D;

    ctx.save();
    ctx.translate(-camX, -camY);
    this.player.render(this.renderer);
    ctx.restore();

    // Invalidate renderer state cache after restore
    this.renderer.invalidateState();
  }

  private renderHUD(): void {
    // Mini-map showing camera position in level
    if (this.currentLevel) {
      this.renderMiniMap();
    }
  }

  private renderMiniMap(): void {
    if (!this.currentLevel) return;

    const mapWidth = 120;
    const mapHeight = 30;
    const mapX = this.canvas.width - mapWidth - 10;
    const mapY = 40;

    // Map border
    this.renderer.drawLine(
      { x: mapX, y: mapY },
      { x: mapX + mapWidth, y: mapY },
      "#333",
      1,
    );
    this.renderer.drawLine(
      { x: mapX + mapWidth, y: mapY },
      { x: mapX + mapWidth, y: mapY + mapHeight },
      "#333",
      1,
    );
    this.renderer.drawLine(
      { x: mapX + mapWidth, y: mapY + mapHeight },
      { x: mapX, y: mapY + mapHeight },
      "#333",
      1,
    );
    this.renderer.drawLine(
      { x: mapX, y: mapY + mapHeight },
      { x: mapX, y: mapY },
      "#333",
      1,
    );

    // Viewport indicator
    const scaleX = mapWidth / this.currentLevel.width;
    const scaleY = mapHeight / this.currentLevel.height;
    const viewX = mapX + this.camera.getX() * scaleX;
    const viewY = mapY + this.camera.getY() * scaleY;
    const viewW = this.canvas.width * scaleX;
    const viewH = this.canvas.height * scaleY;

    this.renderer.drawLine(
      { x: viewX, y: viewY },
      { x: viewX + viewW, y: viewY },
      "#0F0",
      1,
    );
    this.renderer.drawLine(
      { x: viewX + viewW, y: viewY },
      { x: viewX + viewW, y: viewY + viewH },
      "#0F0",
      1,
    );
    this.renderer.drawLine(
      { x: viewX + viewW, y: viewY + viewH },
      { x: viewX, y: viewY + viewH },
      "#0F0",
      1,
    );
    this.renderer.drawLine(
      { x: viewX, y: viewY + viewH },
      { x: viewX, y: viewY },
      "#0F0",
      1,
    );

    // Player position
    const playerPos = this.player.getPosition();
    const playerMapX = mapX + playerPos.x * scaleX;
    const playerMapY = mapY + playerPos.y * scaleY;
    this.renderer.drawCircle(
      { x: playerMapX, y: playerMapY },
      2,
      4,
      CONFIG.colors.player,
    );
  }

  private renderMenu(): void {
    this.renderer.clearFull();
    this.stars.render(this.renderer, 0);

    this.renderer.setGlow(15, CONFIG.colors.player);
    this.renderer.drawText(
      "SKYHAWK",
      { x: this.canvas.width / 2, y: 120 },
      56,
      CONFIG.colors.player,
      "center",
    );
    this.renderer.clearGlow();

    this.renderer.drawText(
      "Assault on the Crystalline Frontier",
      { x: this.canvas.width / 2, y: 160 },
      16,
      "#0AA",
      "center",
    );

    this.renderer.drawText(
      "CONTROLS:",
      { x: this.canvas.width / 2, y: 240 },
      20,
      CONFIG.colors.hudText,
      "center",
    );

    const controls = [
      "← →  ROTATE SHIP",
      "↑    THRUST",
      "↓    REVERSE THRUST",
      "SPACE  FIRE",
      "B      SMART BOMB",
      "P      PAUSE",
      "ESC    MENU",
    ];

    controls.forEach((text, i) => {
      this.renderer.drawText(
        text,
        { x: this.canvas.width / 2, y: 280 + i * 25 },
        14,
        "#0A0",
        "center",
      );
    });

    this.renderer.setGlow(8, CONFIG.colors.player);
    this.renderer.drawText(
      "Press SPACE to launch",
      { x: this.canvas.width / 2, y: 500 },
      20,
      CONFIG.colors.player,
      "center",
    );
    this.renderer.clearGlow();

    this.renderer.drawText(
      "Press E for Level Editor",
      { x: this.canvas.width / 2, y: 540 },
      16,
      "#0AA",
      "center",
    );
  }

  private renderLoading(): void {
    this.renderer.clearFull();
    this.stars.render(this.renderer, 0);

    this.renderer.drawText(
      "LOADING...",
      { x: this.canvas.width / 2, y: this.canvas.height / 2 },
      24,
      CONFIG.colors.player,
      "center",
    );

    if (this.loadingMessage) {
      this.renderer.drawText(
        this.loadingMessage,
        { x: this.canvas.width / 2, y: this.canvas.height / 2 + 40 },
        14,
        "#0A0",
        "center",
      );
    }
  }

  private renderGameOver(): void {
    this.stars.render(this.renderer, 0);

    this.renderer.setGlow(15, "#F00");
    this.renderer.drawText(
      "GAME OVER",
      { x: this.canvas.width / 2, y: this.canvas.height / 2 - 50 },
      48,
      "#F00",
      "center",
    );
    this.renderer.clearGlow();

    this.renderer.drawText(
      `Final Score: ${this.score}`,
      { x: this.canvas.width / 2, y: this.canvas.height / 2 + 10 },
      24,
      CONFIG.colors.hudText,
      "center",
    );

    this.renderer.drawText(
      "Press SPACE to try again",
      { x: this.canvas.width / 2, y: this.canvas.height / 2 + 60 },
      16,
      "#0A0",
      "center",
    );
  }

  private renderPauseOverlay(): void {
    this.renderer.drawText(
      "PAUSED",
      { x: this.canvas.width / 2, y: this.canvas.height / 2 },
      32,
      CONFIG.colors.player,
      "center",
    );
    this.renderer.drawText(
      "Press P to resume",
      { x: this.canvas.width / 2, y: this.canvas.height / 2 + 40 },
      16,
      "#0A0",
      "center",
    );
  }

  private async startNewGame(): Promise<void> {
    this.isLoading = true;
    this.loadingMessage = "Loading level...";

    try {
      // Check for test level from editor
      const urlParams = new URLSearchParams(window.location.search);
      const isTestMode = urlParams.get("test") === "true";

      if (isTestMode) {
        const testLevelJson = localStorage.getItem("testLevel");
        if (testLevelJson) {
          this.currentLevel = JSON.parse(testLevelJson) as LevelData;
          this.loadingMessage = "Loading test level from editor...";
        } else {
          throw new Error("No test level found in localStorage");
        }
      } else {
        // Load level data normally
        this.currentLevel = await LevelLoader.load(this.currentLevelId);
      }

      // Save current level to localStorage for editor access
      localStorage.setItem(
        "lastPlayedLevel",
        JSON.stringify(this.currentLevel),
      );

      // Configure terrain
      this.terrain.loadFromLevelData(this.currentLevel);

      // Configure camera
      this.camera.configure(
        this.currentLevel.width,
        this.currentLevel.height,
        this.currentLevel.scrollDirection,
        this.currentLevel.scrollSpeed,
        this.currentLevel.autoScroll,
      );

      // Spawn player at level spawn point
      const spawnPoint = this.currentLevel.spawnPoint;
      this.player = new Player(spawnPoint.x, spawnPoint.y, this.input);
      // Note: Player shape can be customized by spawn point shape if needed
      this.setupPlayerCallbacks();
      this.player.setInvincible(2);

      // Center camera on player
      this.camera.centerOnImmediate(spawnPoint.x, spawnPoint.y);

      // Spawn level entities (enemies and powerups)
      this.spawnLevelEntities();

      // Reset game state
      this.state = "playing";
      this.score = 0;
      this.lives = CONFIG.player.startingLives;
      this.projectiles = [];
      this.explosions = [];
    } catch (error) {
      console.error("Failed to load level:", error);
      // Fall back to default terrain
      this.terrain = new Terrain(this.canvas.width, this.canvas.height);
      this.camera.configure(
        this.canvas.width,
        this.canvas.height,
        "none",
        0,
        false,
      );

      this.player = new Player(
        this.canvas.width / 2,
        this.canvas.height / 2,
        this.input,
      );
      this.setupPlayerCallbacks();
      this.player.setInvincible(2);

      this.state = "playing";
      this.score = 0;
      this.lives = CONFIG.player.startingLives;
      this.projectiles = [];
      this.explosions = [];
    }

    this.isLoading = false;
    this.updateHUD();
  }

  // Called when player is killed (by enemies, running out of fuel, etc.)
  killPlayer(): void {
    const pos = this.player.getPosition();
    this.explosions.push(new Explosion(pos.x, pos.y, "large"));

    this.lives--;

    if (this.lives <= 0) {
      this.state = "gameover";
    } else {
      // Respawn player at level spawn point
      const spawnPoint = this.currentLevel?.spawnPoint || {
        x: this.canvas.width / 2,
        y: this.canvas.height / 2,
      };
      this.player = new Player(spawnPoint.x, spawnPoint.y, this.input);
      this.setupPlayerCallbacks();
      this.player.setInvincible(2);

      // Reset camera to spawn point
      this.camera.centerOnImmediate(spawnPoint.x, spawnPoint.y);
    }

    this.updateHUD();
  }

  private updateHUD(): void {
    const hud = CONFIG.hud;

    if (this.scoreElement) this.scoreElement.textContent = String(this.score);
    if (this.livesElement) this.livesElement.textContent = String(this.lives);

    // Fuel with warning/critical colors
    const fuel = this.player.getFuel();
    if (this.fuelElement) {
      this.fuelElement.textContent = String(fuel);
      this.fuelElement.classList.remove("warning", "critical");
      if (fuel <= hud.fuelCritical) {
        this.fuelElement.classList.add("critical");
      } else if (fuel <= hud.fuelWarning) {
        this.fuelElement.classList.add("warning");
      }
    }

    // Shield with warning/critical colors
    const shield = this.player.getShieldEnergy();
    if (this.shieldElement) {
      this.shieldElement.textContent = String(shield);
      this.shieldElement.classList.remove("warning", "critical");
      if (shield <= hud.shieldCritical) {
        this.shieldElement.classList.add("critical");
      } else if (shield <= hud.shieldWarning) {
        this.shieldElement.classList.add("warning");
      }
    }

    // Bombs
    if (this.bombsElement) {
      this.bombsElement.textContent = String(this.player.getBombs());
    }
  }

  // Public method to add score
  addScore(points: number): void {
    this.score += points;
  }

  // Public method to load a specific level
  async loadLevel(levelId: string): Promise<void> {
    this.currentLevelId = levelId;
    if (this.state === "playing") {
      await this.startNewGame();
    }
  }

  // Spawn entities from level data
  private spawnLevelEntities(): void {
    this.levelEntities = [];

    if (!this.currentLevel) return;

    // Spawn enemies
    for (const enemyData of this.currentLevel.enemies) {
      console.log(
        `Spawning enemy ${enemyData.type} at (${enemyData.x}, ${enemyData.y}) with shape:`,
        enemyData.shape ? `${enemyData.shape.length} vertices` : "undefined",
      );
      const entity = new LevelEntity(
        enemyData.x,
        enemyData.y,
        enemyData.color || "#ff6b6b",
        "enemy",
        enemyData.type,
        enemyData.shape, // Will use default if undefined
      );
      this.levelEntities.push(entity);
    }

    // Spawn powerups
    for (const powerupData of this.currentLevel.powerups) {
      console.log(
        `Spawning powerup ${powerupData.type} at (${powerupData.x}, ${powerupData.y}) with shape:`,
        powerupData.shape
          ? `${powerupData.shape.length} vertices`
          : "undefined",
      );
      const entity = new LevelEntity(
        powerupData.x,
        powerupData.y,
        powerupData.color || "#4dabf7",
        "powerup",
        powerupData.type,
        powerupData.shape, // Will use default if undefined
      );
      this.levelEntities.push(entity);
    }

    console.log(
      `Spawned ${this.levelEntities.length} level entities (enemies: ${this.currentLevel.enemies.length}, powerups: ${this.currentLevel.powerups.length})`,
    );
  }
}
